/**
 * lesson-plans/$id/print.tsx — A4 print view for a single lesson plan.
 *
 * Loads the plan via lessonPlanQuery(id) and renders <PrintLayout>. The
 * page applies @page A4 CSS and hides navigation/sidebar so the print
 * preview shows only the plan content.
 */

import { createFileRoute, redirect } from '@tanstack/react-router';
import { useQuery, useQueries } from '@tanstack/react-query';
import { PrintLayout } from '../../../components/lessons/PrintLayout.js';
import {
  freshSessionQuery,
  lessonPlanQuery,
  publicCatalogQuery,
} from '../../../lib/api/queries.js';
import { StatePanel } from '../../../components/ui/StatePanel.js';
import { tutorGuideQuery } from '../../../lib/api/queries-tutor-guides.js';
import { stripBold } from '../../../lib/tutor-guides/bold.js';
import type { TutorGuide } from '../../../lib/tutor-guides/schema.js';

/**
 * Catatan bercerita untuk cetak, diringkas dari Panduan Tutor (endpoint
 * tutor-only). Teks polos (pembungkus **bold** dibuang) — PrintLayout merender
 * sebagai text node, tidak pernah HTML.
 */
function printNoteFromGuide(guide: TutorGuide): string {
  return [
    `Konsep: ${guide.concept.name} — ${stripBold(guide.concept.inSimpleWords)}`,
    `Cerita: ${stripBold(guide.storyLink)}`,
    `Tanpa komputer: ${stripBold(guide.unplugged)}`,
  ].join('\n');
}

export const Route = createFileRoute('/lesson-plans/$id/print')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role !== 'tutor' || !user.isApprovedTutor) {
      throw redirect({ to: '/me' });
    }
    return null;
  },
  component: PrintLessonPlanPage,
});

function PrintLessonPlanPage() {
  const { id } = Route.useParams();
  const plan = useQuery(lessonPlanQuery(id));
  const catalog = useQuery(publicCatalogQuery);

  // Panduan Tutor per game di plan (tutor-only API). Game tanpa panduan →
  // tidak ada catatan (tanpa fallback ke teks lama).
  const planGameIds = Array.from(
    new Set(
      (plan.data?.ok ? plan.data.data.items : [])
        .filter((it) => it.source === 'game' && it.gameId)
        .map((it) => it.gameId as string),
    ),
  );
  const guideResults = useQueries({
    queries: planGameIds.map((gameId) => tutorGuideQuery(gameId)),
  });
  const gameNotes = new Map<string, string>();
  planGameIds.forEach((gameId, i) => {
    const res = guideResults[i]?.data;
    if (res?.ok) gameNotes.set(`game:${gameId}`, printNoteFromGuide(res.data.guide));
  });

  if (plan.isLoading) {
    return (
      <main className="page-shell">
        <p>Memuat lesson plan…</p>
      </main>
    );
  }
  if (!plan.data?.ok) {
    return (
      <main className="page-shell">
        <StatePanel
          variant="error"
          title="Lesson plan tidak dapat dimuat"
          description="Coba segarkan halaman ini."
        />
      </main>
    );
  }

  return (
    <main className="page-shell print-shell">
      <div className="print-actions print-screen-only">
        <button
          type="button"
          className="button button-primary"
          onClick={() => window.print()}
          data-testid="print-trigger"
        >
          Cetak sekarang
        </button>
      </div>
      <PrintLayout
        plan={plan.data.data}
        itemTitles={new Map([
          ...(catalog.data?.worksheets ?? []).map((worksheet): [string, string] => [
            `worksheet:${worksheet.worksheetId}`,
            worksheet.judul,
          ]),
          ...(catalog.data?.games ?? []).map((game): [string, string] => [
            `game:${game.gameId}`,
            game.title,
          ]),
        ])}
        gameNotes={gameNotes}
      />
    </main>
  );
}
